'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import { FaKey, FaLock, FaRegFileAlt } from "react-icons/fa";

import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Alert from "react-bootstrap/Alert";

import {isEmpty, pluralize, size} from "@uw-it-sis/lib-react/lib/Utils";

import CreditBadge from "../common/CreditBadge";
import {AcademicRecordList} from "../registration/RegistrationDetails";
import ScheduleBuilderOptionCourse from "./ScheduleBuilderOptionCourse";
import {hasTimeMaskForActivityOption} from "./ScheduleBuilderUtils";
import ScheduleActivityDetailModalStore from "./ScheduleActivityDetailModalStore";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import {getSectionLegendFlags} from "../common/Utils";


/**
 * Component to display the course details popover on the SB calendar
 */
class ScheduleActivityDetailModal extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = new ScheduleActivityDetailModalStore();
        this.toggleAlternates = this.toggleAlternates.bind(this);
    }

    componentDidMount() {
        this.store.mount(this.props);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        if (this.props.indicator !== prevProps.indicator ||
            (this.props.activity && prevProps.activity && this.props.activity.activityOfferingId !== prevProps.activity.activityOfferingId)) {
            this.store.mount(this.props);
        }
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    addToPlanAction(e) {
        e.stopPropagation();
        const formCount = 1 + size(this.props.activity.secondaryOptions);
        this.store.addSectionsToPlan(this.props.activity.termId, this.props.activity.courseCd, formCount);
    }

    cleanupAndHide() {
        this.store.reset();
        this.store.resetData();
        this.props.onHide();
    }

    toggleAlternates(e) {
        e.stopPropagation;
        this.store.toggleShowAlternates();
    }

    buildDoneComponent() {
        let courseList;

        let enrollRestrictionLegend;
        let addCodeLegend;

        let sectionLegendFlags = {
            enrollRestriction: false,
            addCode: false
        };

        if (this.props.registered) {
            let registeredSection = [this.props.activity];
            courseList = <AcademicRecordList sections={registeredSection} view="sb"/>
        } else {
            const item = this.store.preparedScheduleActivity.item;
            const preparedActivities = this.store.preparedScheduleActivity.courseActivities;
            const alternatePrimaryList = this.store.preparedScheduleActivity.alternatePrimaryList;

            if (item) {
                sectionLegendFlags = getSectionLegendFlags(preparedActivities, "enrollmentRestriction")

                courseList = <ScheduleBuilderOptionCourse
                    item={item}
                    preparedActivities={preparedActivities}
                    alternatePrimaryList={alternatePrimaryList}
                    onToggleAlternates={this.toggleAlternates}
                    showAlternates={this.store.data.showAlternates}
                    showCourseHeading={false}
                    hasAlternates={this.store.hasAlternates}
                    showPlanned={true}
                    selectedAlternateCodes={this.store.selectedAlternateCodes}
                    alternatePrimaryActivityIds={this.store.alternatePrimaryActivityIds}
                    allowAddRemoveSection={!this.props.registered}
                    planData={this.props.planData}
                />
            }
        }

        if (sectionLegendFlags.enrollRestriction) {
            enrollRestrictionLegend = <div><FaRegFileAlt className="me-1" aria-hidden={true} /> Section has enrollment restrictions</div>;
        }

        if (sectionLegendFlags.addCode) {
            addCodeLegend = <div><FaKey className="me-1" aria-hidden={true} /> Section requires entry code</div>;
        }

        let courseCd = this.props.registered ? this.props.activity.courseCode.replace(/\s\s+/g, ' ') : this.props.activity.courseCd;
        let courseCredit = this.props.registered ? this.props.activity.credit : this.props.activity.courseCredit;

        let courseHeading =
            <a href={`/course/#/courses/${courseCd}?id=${this.props.activity.courseId}`} className="course-code" target="_blank">
                {courseCd}
            </a>;

        let courseLink =
            <a href={`/course/#/courses/${courseCd}?id=${this.props.activity.courseId}`} className="d-block lead" target="_blank">
                {this.props.activity.courseTitle}
            </a>;

        let lockComponent = null;
        if (this.props.registered) {
            lockComponent =
                <>
                    <FaLock className="text-muted ms-3 align-middle me-1 fs-7" aria-hidden="true" />
                    <small className="align-middle text-uppercase">Registered</small>
                </>
        }

        let courseTitle =
            <div className="flex-grow-1">
                <h3 className="mb-0 d-inline align-middle">
                    {courseHeading}
                </h3>
                <CreditBadge credit={courseCredit} className="ms-2"/>
                {lockComponent}
                <div className="flex-grow-1">
                    {courseLink}
                </div>
                <span className="sr-only">{courseCredit} {pluralize("Credit", courseCredit)}</span>
            </div>;

        let feedbackMessage;
        if (this.store.data.errMsg) {
            feedbackMessage =
                <Alert variant="danger" className="mb-3 mx-0">
                    We couldn't update your plan. {this.store.data.errMsg}
                </Alert>;
        }

        let timeMaskLegend;
        if ( hasTimeMaskForActivityOption(this.props.activity) ) {
            timeMaskLegend = <small className="d-block px-2 mt-2 mb-0">* These sections will ignore any schedule conflicts or date/time filters.</small>;
        }

        let dupEnrollLegend;
        if (this.props.activity.dupEnrolledActivities && this.props.activity.dupEnrolledActivities.length > 0) {
            dupEnrollLegend = <small className="d-block px-2 mt-2 mb-0"><sup>&dagger;</sup> These are primary sections that can be registered concurrently.</small>;
        }

        let footer;
        let plannedLegend;
        if (!this.props.registered) {
            let buttonLabel = "Add to Plan & Update Schedule Options";
            if(this.store.getPlannedActivities(this.props.activity, this.props.planData).length > 0 ){
                buttonLabel = "Update the plan & Schedule options";

                plannedLegend =
                    <div className="px-2 mt-2 mb-0">
                        <span className="d-inline-block bg-planned align-middle me-2"
                              aria-hidden="true"
                              style={{width: '1rem', height: '1rem', border: '1px solid rgba(0, 0, 0, 0.125)'}}>&nbsp;</span>
                        <small className="align-middle">Your planned sections are highlighted in blue.</small>
                    </div>;
            }

            let addToPlanAndUpdateAction = <Button onClick={(e) => this.addToPlanAction(e)}
                                                   disabled={!this.store.dirtyData || this.store.warnDuplicates || appStore.isAdviser()}>{buttonLabel}</Button>;

            let cancel = <Button variant="secondary"
                                 title="Cancel"
                                 onClick={() => this.cleanupAndHide()}>Cancel</Button>;

            footer = <div className="mt-3 mx-0 w-100">
                <ButtonToolbar className="justify-content-end">
                    {addToPlanAndUpdateAction}
                    {cancel}
                </ButtonToolbar>
            </div>
        }

        let modalFooter;
        if (feedbackMessage || footer) {
            modalFooter =
                <Modal.Footer className="pt-0">
                    {feedbackMessage}
                    {footer}
                </Modal.Footer>;
        }

        return <Modal show={this.props.show}
                   onHide={() => this.cleanupAndHide()}
                   aria-labelledby="schedule-activity-detail-modal"
                   centered>
                <Modal.Header closeButton>
                    <Modal.Title id="schedule-activity-detail-modal">{courseTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ListGroup>
                        {courseList}
                    </ListGroup>
                    {enrollRestrictionLegend}
                    {addCodeLegend}
                    {plannedLegend}
                    {timeMaskLegend}
                    {dupEnrollLegend}
                </Modal.Body>
                {modalFooter}
            </Modal>;
    }

    render() {
        return <SimpleState state={this.store.state} doneComponentBuilder={() => this.buildDoneComponent()} />
    }
}

ScheduleActivityDetailModal.propTypes = {
    activity: PropTypes.object.isRequired,
    indicator: PropTypes.string,     // identifier of the source schedule
    show: PropTypes.bool.isRequired,            // show the modal
    onHide: PropTypes.func.isRequired,          // callback on hiding the modal
    registered: PropTypes.bool.isRequired,       // is the course registered
    planData: PropTypes.array.isRequired
}

export default observer(ScheduleActivityDetailModal);